<template>
  <div class="my-resume-page">
    <ResumeDetailsWizard
      :form="form"
      :edit-form="editForm"
      :skills="form.skills"
      :has-base-resume="hasBaseResume"
      :reuse-professional-summary="reuseProfessionalSummary"
      @saved="onWizardSaved"
      @prefill="onPrefill"
      @step-change="$emit('wizard-step-change', $event)"
    />
  </div>
</template>

<script>
import ResumeDetailsWizard from "./ResumeDetailsWizard.vue";
import axios from "@/axios";
import { mapGetters } from "vuex";

function toMonthNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseInt(String(v).trim(), 10);
  return (n >= 1 && n <= 12) ? n : null;
}

function normalizeParsedData(data) {
  return {
    headline: data?.headline ?? "",
    summary: data?.summary ?? "",
    experience: Array.isArray(data?.experience) ? data.experience.map((e) => ({
      title: e.title ?? "",
      company: e.company ?? "",
      location: e.location ?? "",
      startMonth: toMonthNumber(e.startMonth),
      startYear: e.startYear ?? null,
      endMonth: toMonthNumber(e.endMonth),
      endYear: e.endYear ?? null,
      isCurrent: !!e.isCurrent,
      description: e.description ?? "",
      bullets: Array.isArray(e.bullets) ? [...e.bullets] : [],
    })) : [],
    education: Array.isArray(data?.education) ? data.education.map((e) => ({
      degree: e.degree ?? "",
      institution: e.institution ?? "",
      year: e.year ?? "",
      startYear: e.startYear ?? null,
      endYear: e.endYear ?? null,
    })) : [],
  };
}

export default {
  name: "SettingsComponent",
  components: {
    ResumeDetailsWizard,
  },
  props: {
    hasBaseResume: { type: Boolean, default: false },
  },
  emits: ['resume-uploaded', 'profile-updated', 'wizard-step-change'],
  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        skills: [],
      },
      initialFormState: {},
      editForm: { headline: "", summary: "", experience: [], education: [] },
      reuseProfessionalSummary: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
  },
  methods: {
    async fetchResumePreview() {
      try {
        const response = await axios.get("/api/resume/preview");
        if (response.data.hasResume) {
          if (response.data.parsedData?.skills?.length > 0 && this.form.skills.length === 0) {
            this.form.skills = response.data.parsedData.skills;
          }
          if (response.data.parsedData) {
            this.editForm = normalizeParsedData(response.data.parsedData);
          }
          if (typeof response.data.reuseProfessionalSummary === "boolean") {
            this.reuseProfessionalSummary = response.data.reuseProfessionalSummary;
          }
        }
      } catch (error) {
        console.log("No resume uploaded yet");
      }
    },
    onWizardSaved(payload) {
      if (payload?.form) this.form = payload.form;
      if (payload?.editForm) this.editForm = payload.editForm;
      if (typeof payload?.reuseProfessionalSummary === "boolean") {
        this.reuseProfessionalSummary = payload.reuseProfessionalSummary;
      }
      this.initialFormState = JSON.parse(JSON.stringify(this.form));
      this.fetchResumePreview();
      this.$emit("profile-updated");
    },
    onPrefill(extractedData) {
      if (!extractedData) return;
      const name = extractedData.name || extractedData.contact?.name || '';
      const nameParts = name.trim().split(' ');
      if (nameParts.length > 0) this.form.firstName = nameParts[0];
      if (nameParts.length > 1) this.form.lastName = nameParts.slice(1).join(' ');
      if (extractedData.email) this.form.email = extractedData.email;
      const phone = extractedData.contact?.phone ?? extractedData.phone ?? '';
      if (phone) this.form.phone = phone;
      if (Array.isArray(extractedData.skills) && extractedData.skills.length > 0) this.form.skills = extractedData.skills;
      this.editForm = normalizeParsedData(extractedData);
      this.$emit("resume-uploaded");
    },
  },
  async mounted() {
    if (this.user) {
      this.form.email = this.user.email || "";
      this.form.firstName = this.user.firstName || "";
      this.form.lastName = this.user.lastName || "";
      this.form.phone = this.user.phone || "";
      this.form.skills = this.user.skills || [];
    }
    await this.fetchResumePreview();
    this.initialFormState = JSON.parse(JSON.stringify(this.form));
  },
};
</script>

<style scoped>
.my-resume-page {
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
</style>
